import { useInstrumentStore } from "../../state/instrumentStore";
import { useArrangementStore } from "../../state/arrangementStore";
import { DRUMS } from "../../constants/drums";
import { stepsPerBar } from "../../constants/layout";
import { DrumPad } from "./DrumPad";
import { TrackHeader } from "../shared/TrackHeader";
import { useDragPaint } from "../shared/useDragPaint";
import "./DrumTrack.css";

interface DrumTrackProps {
  active: boolean;
}

export function DrumTrack({ active }: DrumTrackProps) {
  const pattern = useInstrumentStore((s) => s.pattern);
  const drumStep = useInstrumentStore((s) => s.step.drums);
  const playing = useInstrumentStore((s) => s.playing);
  const subdivision = useInstrumentStore((s) => s.subdivisions.drums);
  const toggleStep = useInstrumentStore((s) => s.toggleStep);
  const armDrumRange = useInstrumentStore((s) => s.armDrumRange);
  const drumMutes = useInstrumentStore((s) => s.drumMutes);
  const toggleDrumMute = useInstrumentStore((s) => s.toggleDrumMute);
  const setActiveTrack = useInstrumentStore((s) => s.setActiveTrack);
  const setArrangementExpanded = useArrangementStore((s) => s.setExpanded);

  const steps = stepsPerBar(subdivision);
  const activeColumn = playing ? drumStep : -1;

  const dragHandlers = useDragPaint({
    onTap: (row, col) => toggleStep(row, col),
    onArmRange: (row, from, to) => armDrumRange(row, from, to),
  });

  const expand = () => {
    setArrangementExpanded(false);
    setActiveTrack("drums");
  };

  return (
    <div
      className={`drum-track ${active ? "is-active" : "is-collapsed"}`}
      onClick={active ? undefined : expand}
    >
      <TrackHeader track="drums" label="BEATS" tone="green" />
      {active && (
        <div
          className="drum-grid"
          style={{ gridTemplateColumns: `repeat(${steps}, 1fr)` }}
          role="grid"
          aria-label="Drum sequencer"
        >
          {DRUMS.map((drum, rowIdx) => {
            const muted = drumMutes[drum.id];
            return (
              <div
                key={drum.id}
                className={`drum-row ${muted ? "is-muted" : ""}`}
                role="row"
                aria-label={drum.label}
              >
                <button
                  type="button"
                  className="drum-row-label"
                  style={{
                    ["--tone" as string]: `var(--${drum.tone})`,
                    ["--tone-glow" as string]: `var(--glow-${drum.tone})`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDrumMute(drum.id);
                  }}
                  aria-label={
                    muted ? `Unmute ${drum.label}` : `Mute ${drum.label}`
                  }
                  aria-pressed={muted}
                >
                  <span className="drum-row-emoji" aria-hidden="true">
                    {muted ? "🔇" : drum.emoji}
                  </span>
                  <span className="drum-row-name">{drum.label}</span>
                </button>
                <div
                  className="drum-row-cells"
                  style={{ gridTemplateColumns: `repeat(${steps}, 1fr)` }}
                >
                  {Array.from({ length: steps }).map((_, colIdx) => {
                    const armed = pattern[rowIdx]?.[colIdx] === true;
                    const isActive = activeColumn === colIdx;
                    return (
                      <DrumPad
                        key={colIdx}
                        row={rowIdx}
                        column={colIdx}
                        stepIndex={colIdx}
                        drumLabel={drum.label}
                        tone={drum.tone}
                        armed={armed}
                        active={isActive}
                        onPointerDown={dragHandlers.onCellPointerDown(
                          rowIdx,
                          colIdx,
                        )}
                        onPointerEnter={dragHandlers.onCellPointerEnter(
                          rowIdx,
                          colIdx,
                        )}
                        onPointerUp={dragHandlers.onCellPointerUp(
                          rowIdx,
                          colIdx,
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
